import { useCallback, useEffect, useRef, useState } from "react";

type RatingPadProps = {
  durationMs: number;
  autoSubmitEnabled: boolean;
  onSubmit: (rating: { attitudeScore: number; logicScore: number; ratingMs: number }) => void;
};

const GRID_SIZE = 5;
const STEPS = [1, 2, 3, 4, 5] as const;

export function RatingPad({ durationMs, autoSubmitEnabled, onSubmit }: RatingPadProps) {
  const startedAtRef = useRef(performance.now());
  const latestRatingRef = useRef({ attitudeScore: 3, logicScore: 3 });
  const submittedRef = useRef(false);
  const [attitudeScore, setAttitudeScore] = useState(3);
  const [logicScore, setLogicScore] = useState(3);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    latestRatingRef.current = { attitudeScore, logicScore };
  }, [attitudeScore, logicScore]);

  const handleSubmit = useCallback(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setHasSubmitted(true);
    onSubmit({
      ...latestRatingRef.current,
      ratingMs: Math.round(performance.now() - startedAtRef.current)
    });
  }, [onSubmit]);

  useEffect(() => {
    if (!autoSubmitEnabled) return undefined;
    const timeoutId = window.setTimeout(handleSubmit, durationMs);
    return () => window.clearTimeout(timeoutId);
  }, [autoSubmitEnabled, durationMs, handleSubmit]);

  return (
    <section className="rating-workspace" aria-label="二维评分">
      <div className="rating-dual-layout">
        <div className="rating-scales">
          <div className="scale-question scale-question--5">
            <p className="scale-q-label">我认同这条陈述的观点。</p>
            <div className="scale-track-wrap">
              <div className="scale-track-line" aria-hidden="true" />
              <div className="scale-dots">
                {STEPS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`scale-dot${attitudeScore === n ? " selected" : ""}`}
                    aria-pressed={attitudeScore === n}
                    aria-label={`认同度 ${n} 分`}
                    disabled={hasSubmitted}
                    onClick={() => setAttitudeScore(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="scale-poles">
              <span>完全不认同</span>
              <span>完全认同</span>
            </div>
          </div>

          <div className="scale-question scale-question--5">
            <p className="scale-q-label">我认为这条陈述的论述论证是合理的。</p>
            <div className="scale-track-wrap">
              <div className="scale-track-line" aria-hidden="true" />
              <div className="scale-dots">
                {STEPS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`scale-dot${logicScore === n ? " selected" : ""}`}
                    aria-pressed={logicScore === n}
                    aria-label={`论证合理程度 ${n} 分`}
                    disabled={hasSubmitted}
                    onClick={() => setLogicScore(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="scale-poles">
              <span>完全不合理</span>
              <span>非常合理</span>
            </div>
          </div>
        </div>

        <div className="rating-grid-panel">
          <span className="grid-axis-label grid-axis-top">论证合理</span>
          <span className="grid-axis-label grid-axis-left">不认同</span>
          <div className="grid-5x5" aria-hidden="true">
            {Array.from({ length: GRID_SIZE }, (_, row) =>
              Array.from({ length: GRID_SIZE }, (_, col) => {
                const cellLogic = GRID_SIZE - row;
                const cellAttitude = col + 1;
                const isActive = cellAttitude === attitudeScore && cellLogic === logicScore;
                return (
                  <div key={`${row}-${col}`} className={`grid-cell${isActive ? " is-active" : ""}`} />
                );
              })
            )}
          </div>
          <span className="grid-axis-label grid-axis-right">认同</span>
          <span className="grid-axis-label grid-axis-bottom">论证不合理</span>
        </div>
      </div>

      <button className="primary-button" type="button" onClick={handleSubmit} disabled={hasSubmitted}>
        提交
      </button>
    </section>
  );
}
